import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import { observer } from 'mobx-react';
import { FileUploadOutlined as UploadIcon } from '@mui/icons-material';
import * as Sentry from '@sentry/browser';

import { PrimaryButton, SaveButton } from '../../../../../../../../../components/Button/ActionButtons';
import FileSelectButton from '../../../../../../../../../components/FileSelectButton';
import { StyledTextField, StyledTextFieldContainer } from '../../../../../../../../../components/Inputs';
import InputLabel from '../../../../../../../../../components/v2/InputLabel';
import useComponentMounted from '../../../../../../../../../hooks/useComponentMounted';
import useLogger from '../../../../../../../../../hooks/useLogger';
import useStorage from '../../../../../../../../../hooks/useStorage';
import Product from '../../../../../../../../../Model/Product';
import { uploadAppIcon } from '../../../../../../../../../utils/appIconUpload';
import { pathPlaceholder, storagePaths } from '../../../../../../../../../utils/firebasePaths';
import useToast from '../../../../../../../../hooks/useToast';
import {
  LogoPreview,
  LogoRow,
  Panel,
  Row,
} from './styles';
import texts from './texts.json';

const SAVED_EVENT = 'productAppIconSaved';

/**
 * A product's own mobile app icon and in-app logo, for admins. The mobile app shows them instead of the coach's to
 * the clients it brands with this product; a value left empty follows the coach's.
 */
const ProductAppIcon = ({ product }) => {
  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();
  const { logEvent } = useLogger();
  const { getBlobUrl, uploadAttachment } = useStorage();
  const [iconName, setIconName] = useState(product.appIconName);
  const [savedLogoUrl, setSavedLogoUrl] = useState('');
  // The picked logo as `{ file, url }`, until it is saved or dropped.
  const [pickedLogo, setPickedLogo] = useState(null);
  const [removesLogo, setRemovesLogo] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const loadSavedLogo = async () => {
      const url = product.webAppIconRef ? await getBlobUrl(product.webAppIconRef) : '';
      if (isComponentMountedRef.current) {
        setSavedLogoUrl(url);
      }
    };
    loadSavedLogo();
  }, [getBlobUrl, isComponentMountedRef, product.webAppIconRef]);

  const nextIconName = iconName.trim();
  const keepsLogo = !removesLogo && !!product.webAppIconRef;
  const logoUrl = pickedLogo?.url || (keepsLogo ? savedLogoUrl : '');
  const hasChanges = nextIconName !== product.appIconName || !!pickedLogo || (removesLogo && !!product.webAppIconRef);

  const pickLogo = ({ target: { files: [file] } }) => {
    if (file) {
      setPickedLogo({ file, url: URL.createObjectURL(file) });
      setRemovesLogo(false);
    }
  };

  // Drops the picked file, or marks the saved logo for removal, so the product follows the coach's.
  const removeLogo = () => {
    setPickedLogo(null);
    setRemovesLogo(true);
  };

  const handleSave = async () => {
    setIsProcessing(true);
    try {
      let webAppIconRef = keepsLogo ? product.webAppIconRef : '';
      if (pickedLogo) {
        // Beside the coach's own logo: the folder is already readable by clients and writable by admins.
        const storagePath = format(storagePaths.APP_CUSTOM_ICONS, { [pathPlaceholder.COACH_ID]: product.coach });
        webAppIconRef = await uploadAppIcon(pickedLogo.url, uploadAttachment, pickedLogo.file, storagePath);
      }
      await product.setAppIcon({ appIconName: nextIconName, webAppIconRef });
      logEvent(SAVED_EVENT, {
        productId: product.id,
        coachId: product.coach,
        hasIconName: !!nextIconName,
        hasLogo: !!webAppIconRef,
      });
      showToast(format(texts.saveSuccessful, { product: product.name }));
      if (isComponentMountedRef.current) {
        setPickedLogo(null);
        setRemovesLogo(false);
      }
    } catch (error) {
      Sentry.captureException(error, { extra: { productId: product.id } });
      showToast(texts.saveFailed, { error: true });
    }
    if (isComponentMountedRef.current) {
      setIsProcessing(false);
    }
  };

  return (
    <Panel title={texts.title}>
      <Row>
        <StyledTextFieldContainer>
          <InputLabel>{texts.iconName}</InputLabel>
          <StyledTextField
            value={iconName}
            onChange={(event) => setIconName(event.target.value)}
            placeholder={texts.coachs}
            inputProps={{ 'aria-label': texts.iconName }}
          />
        </StyledTextFieldContainer>
        <StyledTextFieldContainer>
          <InputLabel>{texts.logo}</InputLabel>
          <LogoRow>
            {!!logoUrl && <LogoPreview src={logoUrl} alt={texts.logo} />}
            <FileSelectButton
              ButtonComponent={PrimaryButton}
              onSelect={pickLogo}
              multiple={false}
              extraInputProps={{ accept: 'image/svg+xml' }}
              extraButtonProps={{ variant: 'info', size: 'small', icon: <UploadIcon /> }}
            >
              {texts.chooseLogo}
            </FileSelectButton>
            {!!logoUrl && (
              <PrimaryButton onClick={removeLogo} variant="info" size="small">
                {texts.removeLogo}
              </PrimaryButton>
            )}
          </LogoRow>
        </StyledTextFieldContainer>
        <SaveButton onClick={handleSave} disabled={!hasChanges || isProcessing} $fitToContent>
          {texts.save}
        </SaveButton>
      </Row>
    </Panel>
  );
};

ProductAppIcon.propTypes = {
  product: PropTypes.instanceOf(Product).isRequired,
};

export default observer(ProductAppIcon);
